import { useState } from "react";
import toast from "react-hot-toast";
import { get } from "lodash";
import {
  DotsVerticalIcon,
  SearchIcon,
  TrashIcon,
  DotsHorizontalIcon,
} from "@heroicons/react/outline";
import { useIsMobile } from "routes/group/hooks/useIsMobile";
import { useParams } from "react-router-dom";
import useQueryParams from "routes/group/hooks/useQueryParams";
import { useMoveSingleAttendant } from "routes/group/hooks/useMoveSingleAttendant";
import { useMoveMultiAttendant } from "routes/group/hooks/useMoveMultiAttendant";
import {
  ActionMultiAttendantPayload,
  ActionSingleAttendantPayload,
  AttendantType,
  MultiMoveAttendantPayload,
  PagingType,
  SingleMoveAttendantPayload,
} from "routes/group/types";
import MultiMoveErrorModal, {
  MultiMoveErrorType,
} from "../MultiMoveErrorModal";
import { SelectedAttendantType } from "routes/group/pages/edit";
import { formatNumber, isHaveActionPermission } from "utills/helpers";
import Button from "components/button";
import Pagination from "components/pagination";
import { Tag } from "components/tag";
import { Label } from "components/label";
import Input from "components/input";
import Select from "components/select";
import { DropdownMenu } from "components/dropdownMenu";
import { ConfirmModal } from "components/confirmModal";
import SingleMoveModal from "../SingleMoveModal";
import MultiMoveModal from "../MultiMoveModal";
import AttendantDetailForm from "../attedantDetailForm";

export interface FilterType {
  page: number;
  q: string;
  group_id: string;
  payment_status: string;
}

interface PropType {
  mode: "create" | "edit" | "detail";
  data: AttendantType[];
  paging: PagingType;
  handleChangePage: (page: number) => void;
  filter: FilterType;
  handleChangeFilter: (key: string, value: string) => void;
  onSubmit: (data: FilterType) => void;

  // Option props for edit case
  handleActionSingle?: (
    group_id: string,
    payload: ActionSingleAttendantPayload
  ) => Promise<void>;
  selectedAttendantIds?: SelectedAttendantType[];
  toggleSelectAttendantId?: (item: SelectedAttendantType) => void;
  toggleSelectMultiAttendantId?: (items: SelectedAttendantType[]) => void;
  handleClearAllSelectAttendantId?: () => void;
  handleActionMulti?: (
    group_id: string,
    payload: ActionMultiAttendantPayload
  ) => Promise<void>;
  handleRefetchGroupAttendant?: () => void;
  handleRefetchDetailGroup?: () => void;
}

const AttendantDataTable = (props: PropType) => {
  const {
    mode,
    data,
    paging,
    handleChangePage,
    filter,
    handleChangeFilter,
    onSubmit,
    handleActionSingle,
    selectedAttendantIds,
    toggleSelectAttendantId,
    toggleSelectMultiAttendantId,
    handleClearAllSelectAttendantId,
    handleActionMulti,
    handleRefetchGroupAttendant,
    handleRefetchDetailGroup,
  } = props;

  const PAGE_SIZE = 10;

  const PAYMENT_STATUS_OPTIONS = [
    { value: "", label: "All", color: "" },
    { value: "1", label: "Unpaid", color: "pink" },
    { value: "2", label: "Paid", color: "green" },
  ];

  const ATTENDANT_ACTION = {
    REMOVE: {
      value: 0,
      label: "Remove",
    },
    TICK_PAID: {
      value: 1,
      label: "Tick paid",
    },
    TICK_UNPAID: {
      value: 2,
      label: "Tick Unpaid",
    },
    MOVE: {
      value: 10, // Đảm bảo không trùng với các action trong tương lai
      label: "Move",
    },
    // TICK_PAID_ALL: {
    //   value: 3,
    //   label: "Tick Paid All",
    // },
    // TICK_UNPAID_ALL: {
    //   value: 4,
    //   label: "Tick Unpaid All",
    // },
  };

  const MULTI_ATTENDANTS_ACTION = {
    REMOVE: {
      value: 0,
      label: "Remove",
    },
    TICK_PAID: {
      value: 1,
      label: "Tick paid",
    },
    TICK_UNPAID: {
      value: 2,
      label: "Tick Unpaid",
    },
    MOVE: {
      value: 10, // Đảm bảo không trùng với các action trong tương lai
      label: "Move",
    },
  };

  const params = useParams();
  const isMobile = useIsMobile(1024);
  const { searchParams, setQueryParams } = useQueryParams();

  const group_id = params?.id ?? "";
  const event_id = searchParams.get("event_id") ?? "";

  const {
    mutation: moveSingleAttendantMutation,
    loading: moveSingleAttendantLoading,
  } = useMoveSingleAttendant();

  const {
    mutation: moveMultiAttendantMutation,
    loading: moveMultiAttendantLoading,
  } = useMoveMultiAttendant();

  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
  const [singleAttendant, setSingleAttendant] = useState<{
    actionValue: number;
    actionName: string;
    data: AttendantType;
  } | null>(null);

  const [openMultiConfirmModal, setOpenMultiConfirmModal] =
    useState<boolean>(false);
  const [multiAttendant, setMultiAttendant] = useState<{
    actionValue: number;
    actionName: string;
    members: SelectedAttendantType[];
  } | null>(null);

  const [moveAttendant, setMoveAttendant] = useState<AttendantType | null>(
    null
  );
  const [openSingleMoveModal, setOpenSingleMoveModal] =
    useState<boolean>(false);

  const [moveAttendants, setMoveAttendants] = useState<SelectedAttendantType[]>(
    []
  );
  const [openMultiMoveModal, setOpenMultiMoveModal] = useState<boolean>(false);

  const [attendantId, setAttendantId] = useState<string>("");
  const [openDetailAttendantModal, setOpenDetailAttendantModal] =
    useState<boolean>(false);

  const [multiMoveError, setMultiMoveError] = useState<MultiMoveErrorType[]>(
    []
  );
  const [openMultiMoveErrorModal, setOpenMultiMoveErrorModal] =
    useState<boolean>(false);

  const handleFormatPaymentStatus = (value: string) => {
    return (
      PAYMENT_STATUS_OPTIONS.find((status) => status.value === value) ?? null
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(filter);
  };

  // ========== Single attendant handle ==========

  const handleCheckRenderAction = (
    attendantValue: number,
    actionValue: number
  ) => {
    if (
      mode !== "edit" ||
      !isHaveActionPermission("", "group_buy_management", "edit")
    )
      return false;

    // Remove action check
    if (
      actionValue === ATTENDANT_ACTION.REMOVE.value &&
      !isHaveActionPermission("", "group_buy_management", "delete")
    )
      return false;

    return true;
  };

  const handleActionSingleAttendant = (
    attendant: AttendantType,
    actionValue: number,
    actionName: string
  ) => {
    setSingleAttendant({
      data: attendant,
      actionValue: actionValue,
      actionName: actionName,
    });
    setOpenConfirmModal(true);
  };

  const handleConfirmSingleAttendantAction = async () => {
    // console.log(singleAttendant);

    if (!group_id || group_id == "") {
      toast.error("Group not found");
      return;
    }

    if (!singleAttendant) {
      toast.error("Attendant not found");
      return;
    }

    if (handleActionSingle && singleAttendant) {
      const payload: ActionSingleAttendantPayload = {
        action: singleAttendant.actionValue,
        member_id: singleAttendant.data?.member_id,
        registered_product_id: singleAttendant.data?.registered_product_id,
      };

      handleActionSingle(group_id, payload);
    }
    setSingleAttendant(null);
  };

  // ========== Multiple attendant handle ==========

  const handleGetAttendantChecked = (
    memberId: string,
    selectedAttendantIds: SelectedAttendantType[]
  ): boolean => {
    if (!selectedAttendantIds?.length) return false;

    return selectedAttendantIds.some((item) => item.member_id === memberId);
  };

  const isAllAttendantsChecked = (
    selected: SelectedAttendantType[],
    memberIds: string[]
  ) => {
    if (!selected.length || !memberIds.length) return false;

    const selectedSet = new Set(selected.map((item) => item.member_id));

    return memberIds.every((id) => selectedSet.has(id));
  };

  const handleCheckAllAttendants = (items: SelectedAttendantType[]) => {
    if (toggleSelectMultiAttendantId) toggleSelectMultiAttendantId(items);
  };

  const handleCheckRenderMultiAction = (actionValue: number) => {
    if (
      mode !== "edit" ||
      !isHaveActionPermission("", "group_buy_management", "edit")
    )
      return false;

    // Remove action check
    if (
      actionValue === ATTENDANT_ACTION.REMOVE.value &&
      !isHaveActionPermission("", "group_buy_management", "delete")
    )
      return false;

    return true;
  };

  const handleActionMultiAttendant = (
    attendants: SelectedAttendantType[],
    actionValue: number,
    actionName: string
  ) => {
    setMultiAttendant({
      actionValue: actionValue,
      actionName: actionName,
      members: attendants,
    });

    setOpenMultiConfirmModal(true);
  };

  const handleConfirmMultiAttendantAction = async () => {
    // console.log(multiAttendant);

    if (!group_id || group_id == "") {
      toast.error("Group not found");
      return;
    }

    if (!multiAttendant?.members) {
      toast.error("Attendants not found");
      return;
    }

    if (handleActionMulti && multiAttendant) {
      const payload: ActionMultiAttendantPayload = {
        action: multiAttendant.actionValue,
        items: multiAttendant.members,
      };

      handleActionMulti(group_id, payload);
    }

    setMultiAttendant(null);
  };

  // ========== Single move attendant handle ==========

  const handleMoveSingle = (attendant: AttendantType) => {
    setMoveAttendant(attendant);
    setOpenSingleMoveModal(true);
  };

  const handleCloseSingleModeModal = () => {
    setMoveAttendant(null);
    setOpenSingleMoveModal(false);
  };

  const handleSubmitSingleMove = async (
    source_group_id: string,
    payload: SingleMoveAttendantPayload
  ) => {
    console.log({ source_group_id, payload });

    const res = await moveSingleAttendantMutation(source_group_id, payload);

    if (res.success) {
      toast.success("Move attendant successfully");

      // Refetch detail group
      if (handleRefetchDetailGroup) handleRefetchDetailGroup();

      // Refetch attendant data
      if (handleRefetchGroupAttendant) handleRefetchGroupAttendant();

      handleCloseSingleModeModal();
    }
  };

  // ========== Multi move attendant handle ==========

  const handleMoveMulti = (attendants: SelectedAttendantType[]) => {
    setMoveAttendants(attendants);
    setOpenMultiMoveModal(true);
  };

  const handleCloseMultiModeModal = () => {
    setMoveAttendants([]);
    setOpenMultiMoveModal(false);
  };

  const handleSubmitMultiMove = async (
    source_group_id: string,
    payload: MultiMoveAttendantPayload
  ) => {
    console.log({ source_group_id, payload });

    const res = await moveMultiAttendantMutation(source_group_id, payload);

    // Full success
    if (res.success && res.error_count === 0) {
      toast.success("Move attendants successfully");

      // Refetch detail group
      if (handleRefetchDetailGroup) handleRefetchDetailGroup();

      // Refetch attendant data
      if (handleRefetchGroupAttendant) handleRefetchGroupAttendant();

      // Clear selected attendants state
      if (handleClearAllSelectAttendantId) handleClearAllSelectAttendantId();
    }

    // Both success & failed
    if (res.success && res.success_count !== 0 && res.error_count !== 0) {
      toast(res.message);

      // Refetch detail group
      if (handleRefetchDetailGroup) handleRefetchDetailGroup();

      // Refetch attendant data
      if (handleRefetchGroupAttendant) handleRefetchGroupAttendant();

      // Clear selected attendants state
      if (handleClearAllSelectAttendantId) handleClearAllSelectAttendantId();
    }

    // Have error list
    if (res.error_count !== 0 && res.error) {
      setMultiMoveError(res.error);
      setOpenMultiMoveErrorModal(true);
    }

    handleCloseMultiModeModal();
  };

  const handleCloseMultiMoveErrorModal = () => {
    setMultiMoveError([]);
    setOpenMultiMoveErrorModal(false);
  };

  // ========== Handle view detail attendant ==========

  const handleViewDetailAttendant = (attendant_id: string) => {
    setAttendantId(attendant_id);
    setOpenDetailAttendantModal(true);
  };

  const handleCloseDetailAttendantModal = () => {
    setAttendantId("");
    setOpenDetailAttendantModal(false);
  };

  return (
    <section className="flex flex-col gap-5">
      {/* Single Confirm Modal */}
      {singleAttendant && (
        <ConfirmModal
          key="confirm-single-attendant-action"
          title={"Confirm"}
          content={""}
          customContent={
            <div className="mt-3 flex flex-col gap-1 text-[14px]">
              <span>
                Are you sure to{" "}
                <span className="font-semibold">
                  {singleAttendant.actionName}
                </span>{" "}
                this attendant?
              </span>

              <div className="mt-3 w-full flex flex-col items-start gap-1">
                <span>Full Name: {singleAttendant.data?.full_name}</span>
                <span>Email/Phone: {singleAttendant.data?.email}</span>
                <span>Phone: {singleAttendant.data?.Mobile}</span>
              </div>
            </div>
          }
          open={openConfirmModal}
          onConfirm={handleConfirmSingleAttendantAction}
          onClose={() => setOpenConfirmModal(false)}
        />
      )}

      {/* Single Move Modal */}
      <SingleMoveModal
        groupId={group_id}
        eventId={event_id}
        open={openSingleMoveModal}
        loading={moveSingleAttendantLoading}
        attendant={moveAttendant}
        handleClose={handleCloseSingleModeModal}
        handleSubmitSingleMove={handleSubmitSingleMove}
      />

      {/* Multi Confirm Modal */}
      {multiAttendant && (
        <ConfirmModal
          key="confirm-multi-attendant-action"
          title={"Confirm"}
          content={""}
          customContent={
            <div className="mt-3 flex flex-col gap-1 text-[14px]">
              <span>
                Are you sure to{" "}
                <span className="font-semibold">
                  {multiAttendant.actionName}
                </span>{" "}
                this attendants?
              </span>
            </div>
          }
          open={openMultiConfirmModal}
          onConfirm={handleConfirmMultiAttendantAction}
          onClose={() => setOpenMultiConfirmModal(false)}
        />
      )}

      {/* Multi Move Modal */}
      <MultiMoveModal
        groupId={group_id}
        eventId={event_id}
        open={openMultiMoveModal}
        loading={moveMultiAttendantLoading}
        attendants={moveAttendants}
        handleClose={handleCloseMultiModeModal}
        handleSubmitMultiMove={handleSubmitMultiMove}
      />

      {/* Multi Move Error Modal */}
      <MultiMoveErrorModal
        open={openMultiMoveErrorModal}
        data={multiMoveError}
        handleClose={handleCloseMultiMoveErrorModal}
      />

      {/* Detail Attendant Modal */}
      <AttendantDetailForm
        id={attendantId}
        open={openDetailAttendantModal}
        handleClose={handleCloseDetailAttendantModal}
      />

      {/* Filter Bar */}
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="w-full flex flex-wrap items-end gap-3 mb-5"
      >
        <div className="w-full md:w-[400px]">
          <Label id="search" name="Search" />
          <div className="mt-2">
            <Input
              className="px-2 py-2"
              label="Search"
              id="search"
              type="text"
              placeholder="Search by Full Name, Email, Phone"
              value={filter.q}
              onChange={(e: any) => {
                handleChangeFilter("q", e.target.value);
              }}
            />
          </div>
        </div>

        <div className="w-full md:w-[400px]">
          <Label id="payment_status" name="Payment Status" />
          <div className="mt-2">
            <Select
              options={PAYMENT_STATUS_OPTIONS}
              isClearable={true}
              id="payment_status"
              placeholder="Select option"
              className="py-0.5"
              value={PAYMENT_STATUS_OPTIONS.find(
                (d) => d?.value === filter.payment_status
              )}
              onChange={(selected: { label: string; value: string }) => {
                handleChangeFilter("payment_status", selected?.value ?? "");
              }}
            />
          </div>
        </div>

        <Button
          type="submit"
          className="w-[100px] bg-white rounded-md px-3 text-brand-500 py-2
                        border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85"
        >
          <SearchIcon className="w-3 h-4 text-brand-500 mr-2" /> Search
        </Button>
      </form>

      {/* Multi Action Bar */}
      {mode === "edit" &&
        isHaveActionPermission("", "group_buy_management", "edit") &&
        selectedAttendantIds &&
        selectedAttendantIds?.length !== 0 && (
          <>
            <div className="w-full h-[1px] bg-gray-200" />

            <div className="w-full mb-5 flex flex-wrap items-center gap-3">
              <span className="text-[15px]">
                <span className="font-semibold">Selected attendants:</span>{" "}
                {selectedAttendantIds?.length}
              </span>

              <div className="w-[1px] h-5 bg-gray-300 mx-3"></div>

              {handleClearAllSelectAttendantId && (
                <Button
                  className="w-11 h-11 sm:w-auto"
                  onClick={() => {
                    handleClearAllSelectAttendantId();
                  }}
                >
                  <TrashIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">Clear All</span>
                </Button>
              )}

              <DropdownMenu
                position="bottom"
                label={
                  <div
                    className="w-11 h-11 bg-white rounded-md px-3 text-brand-500 py-2
                            border-solid border-brand-500 border-[1px] flex items-center
                            text-[0.875rem] font-[600]
                            hover:bg-white active:bg-white active:opacity-85 sm:w-auto"
                  >
                    <DotsHorizontalIcon className="size-4 text-brand-500" />{" "}
                    <span className="hidden sm:block sm:ml-2">
                      More Actions
                    </span>
                  </div>
                }
                items={[
                  ...(handleCheckRenderMultiAction(
                    MULTI_ATTENDANTS_ACTION.TICK_PAID.value
                  )
                    ? [
                        {
                          label: "Tick Paid",
                          onClick: () => {
                            handleActionMultiAttendant(
                              selectedAttendantIds,
                              MULTI_ATTENDANTS_ACTION.TICK_PAID.value,
                              MULTI_ATTENDANTS_ACTION.TICK_PAID.label
                            );
                          },
                        },
                      ]
                    : []),

                  ...(handleCheckRenderMultiAction(
                    MULTI_ATTENDANTS_ACTION.TICK_UNPAID.value
                  )
                    ? [
                        {
                          label: "Tick Unpaid",
                          onClick: () => {
                            handleActionMultiAttendant(
                              selectedAttendantIds,
                              MULTI_ATTENDANTS_ACTION.TICK_UNPAID.value,
                              MULTI_ATTENDANTS_ACTION.TICK_UNPAID.label
                            );
                          },
                        },
                      ]
                    : []),

                  ...(handleCheckRenderMultiAction(
                    MULTI_ATTENDANTS_ACTION.MOVE.value
                  )
                    ? [
                        {
                          label: "Move",
                          onClick: () => {
                            handleMoveMulti(selectedAttendantIds);
                          },
                        },
                      ]
                    : []),

                  ...(handleCheckRenderMultiAction(
                    MULTI_ATTENDANTS_ACTION.REMOVE.value
                  )
                    ? [
                        {
                          label: "Remove",
                          onClick: () => {
                            handleActionMultiAttendant(
                              selectedAttendantIds,
                              MULTI_ATTENDANTS_ACTION.REMOVE.value,
                              MULTI_ATTENDANTS_ACTION.REMOVE.label
                            );
                          },
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          </>
        )}

      {isMobile ? (
        <div className="mb-6 w-full flex flex-col gap-[8px]">
          {data?.map((item: AttendantType, index: number) => {
            return (
              <div
                key={index}
                className="w-full bg-white p-[16px] rounded-[8px] shadow-md"
              >
                <div className="w-full flex items-center justify-between">
                  {mode === "edit" ? (
                    isHaveActionPermission(
                      "",
                      "group_buy_management",
                      "edit"
                    ) && (
                      <td className="td">
                        <Input
                          type="checkbox"
                          id="change_pickup_method"
                          checked={handleGetAttendantChecked(
                            get(item, "member_id", ""),
                            selectedAttendantIds ?? []
                          )}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            if (toggleSelectAttendantId) {
                              toggleSelectAttendantId({
                                member_id: get(item, "member_id", ""),
                                registered_product_id: get(
                                  item,
                                  "registered_product_id",
                                  ""
                                ),
                              });
                            }
                          }}
                        />
                      </td>
                    )
                  ) : (
                    <div className="opacity-0">action</div>
                  )}

                  <div className="flex items-center gap-2">
                    <DropdownMenu
                      label={<DotsVerticalIcon className="w-4" />}
                      items={[
                        ...(isHaveActionPermission(
                          "",
                          "group_buy_management",
                          "view"
                        )
                          ? [
                              {
                                label: "View",
                                onClick: () => {
                                  handleViewDetailAttendant(
                                    get(item, "id", "")
                                  );
                                },
                              },
                            ]
                          : []),

                        ...(handleCheckRenderAction(
                          item?.payment_status,
                          ATTENDANT_ACTION.TICK_PAID.value
                        )
                          ? [
                              {
                                label: "Tick Paid",
                                onClick: () => {
                                  handleActionSingleAttendant(
                                    item,
                                    ATTENDANT_ACTION.TICK_PAID.value,
                                    ATTENDANT_ACTION.TICK_PAID.label
                                  );
                                },
                              },
                            ]
                          : []),

                        ...(handleCheckRenderAction(
                          item?.payment_status,
                          ATTENDANT_ACTION.TICK_UNPAID.value
                        )
                          ? [
                              {
                                label: "Tick Unpaid",
                                onClick: () => {
                                  handleActionSingleAttendant(
                                    item,
                                    ATTENDANT_ACTION.TICK_UNPAID.value,
                                    ATTENDANT_ACTION.TICK_UNPAID.label
                                  );
                                },
                              },
                            ]
                          : []),

                        // ...(handleCheckRenderAction(
                        //   item?.payment_status,
                        //   ATTENDANT_ACTION.TICK_PAID_ALL.value
                        // )
                        //   ? [
                        //       {
                        //         label: "Tick Paid All",
                        //         onClick: () => {
                        //           handleActionSingleAttendant(
                        //             item,
                        //             ATTENDANT_ACTION.TICK_PAID_ALL.value,
                        //             ATTENDANT_ACTION.TICK_PAID_ALL.label
                        //           );
                        //         },
                        //       },
                        //     ]
                        //   : []),

                        // ...(handleCheckRenderAction(
                        //   item?.payment_status,
                        //   ATTENDANT_ACTION.TICK_UNPAID_ALL.value
                        // )
                        //   ? [
                        //       {
                        //         label: "Tick Unpaid All",
                        //         onClick: () => {
                        //           handleActionSingleAttendant(
                        //             item,
                        //             ATTENDANT_ACTION.TICK_UNPAID_ALL.value,
                        //             ATTENDANT_ACTION.TICK_UNPAID_ALL.label
                        //           );
                        //         },
                        //       },
                        //     ]
                        //   : []),

                        ...(handleCheckRenderAction(
                          item?.payment_status,
                          ATTENDANT_ACTION.REMOVE.value
                        )
                          ? [
                              {
                                label: "Remove",
                                onClick: () => {
                                  handleActionSingleAttendant(
                                    item,
                                    ATTENDANT_ACTION.REMOVE.value,
                                    ATTENDANT_ACTION.REMOVE.label
                                  );
                                },
                              },
                            ]
                          : []),
                      ]}
                    />
                  </div>
                </div>

                <div className="w-full mt-[16px] flex flex-col gap-[8px]">
                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      No
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {(get(paging, "current_page", 1) - 1) * PAGE_SIZE +
                        1 +
                        index}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Full Name
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {get(item, "full_name", "")}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Gender
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {get(item, "gender.gender_en", "")}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Phone
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {get(item, "Mobile", "")}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Email
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {get(item, "email", "")}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Distance
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {get(item, "distance", "")}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Ticket Price
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {formatNumber(get(item, "ticket_price", 0))}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Discount
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {formatNumber(get(item, "discount", 0))}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Total
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {formatNumber(get(item, "total", 0))}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Payment Status
                    </span>
                    <span className="w-[70%] text-right">
                      <Tag
                        text={
                          handleFormatPaymentStatus(
                            get(item, "payment_status", "").toString()
                          )?.label ?? ""
                        }
                        type={
                          handleFormatPaymentStatus(
                            get(item, "payment_status", "").toString()
                          )?.color ?? ("" as any)
                        }
                      />
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flow-root">
          <div className="relative overflow-x-auto min-w-full">
            <table className="mb-6 w-full divide-y divide-gray-300 bg-magnolia-500 rounded-md">
              <thead>
                <tr>
                  {mode === "edit" &&
                    isHaveActionPermission(
                      "",
                      "group_buy_management",
                      "edit"
                    ) && (
                      <th scope="col" className="th">
                        <Input
                          type="checkbox"
                          id="change_pickup_method"
                          checked={isAllAttendantsChecked(
                            selectedAttendantIds ?? [],
                            data?.map((item) => item?.member_id)
                          )}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            if (toggleSelectAttendantId) {
                              handleCheckAllAttendants(
                                data?.map((item) => {
                                  return {
                                    member_id: get(item, "member_id", ""),
                                    registered_product_id: get(
                                      item,
                                      "registered_product_id",
                                      ""
                                    ),
                                  };
                                })
                              );
                            }
                          }}
                        />
                      </th>
                    )}

                  <th
                    scope="col"
                    className="th py-3.5 pl-4 pr-3 sm:pl-6 lg:pl-8"
                  >
                    No
                  </th>

                  <th scope="col" className="th">
                    Full Name
                  </th>

                  <th scope="col" className="th">
                    Gender
                  </th>

                  <th scope="col" className="th">
                    Phone
                  </th>

                  <th scope="col" className="th">
                    Email
                  </th>

                  <th scope="col" className="th">
                    Distance
                  </th>

                  <th scope="col" className="th">
                    Ticket Price
                  </th>

                  <th scope="col" className="th">
                    Discount
                  </th>

                  <th scope="col" className="th">
                    Total
                  </th>

                  <th scope="col" className="th">
                    Payment Status
                  </th>

                  <th scope="col" className="th w-28">
                    <span className="sr-only">Action</span>
                    <p className="">Action</p>
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 bg-white">
                {data?.map((item: AttendantType, index: number) => {
                  return (
                    <tr key={index}>
                      {mode === "edit" &&
                        isHaveActionPermission(
                          "",
                          "group_buy_management",
                          "edit"
                        ) && (
                          <td className="td">
                            <Input
                              type="checkbox"
                              id="change_pickup_method"
                              checked={handleGetAttendantChecked(
                                get(item, "member_id", ""),
                                selectedAttendantIds ?? []
                              )}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                if (toggleSelectAttendantId) {
                                  toggleSelectAttendantId({
                                    member_id: get(item, "member_id", ""),
                                    registered_product_id: get(
                                      item,
                                      "registered_product_id",
                                      ""
                                    ),
                                  });
                                }
                              }}
                            />
                          </td>
                        )}

                      <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                        {(get(paging, "current_page", 1) - 1) * PAGE_SIZE +
                          1 +
                          index}
                      </td>

                      <td className="td min-w-[230px]">
                        {get(item, "full_name", "")}
                      </td>

                      <td className="td min-w-[150px]">
                        {get(item, "gender.gender_en", "")}
                      </td>

                      <td className="td min-w-[160px]">
                        {get(item, "Mobile", "")}
                      </td>

                      <td className="td min-w-[250px]">
                        {get(item, "email", "")}
                      </td>

                      <td className="td min-w-[180px]">
                        {get(item, "distance", "")}
                      </td>

                      <td className="td min-w-[150px]">
                        {formatNumber(get(item, "ticket_price", 0))}
                      </td>

                      <td className="td min-w-[150px]">
                        {formatNumber(get(item, "discount", 0))}
                      </td>

                      <td className="td min-w-[150px]">
                        {formatNumber(get(item, "total", 0))}
                      </td>

                      <td className="td min-w-40">
                        <Tag
                          text={
                            handleFormatPaymentStatus(
                              get(item, "payment_status", "").toString()
                            )?.label ?? ""
                          }
                          type={
                            handleFormatPaymentStatus(
                              get(item, "payment_status", "").toString()
                            )?.color ?? ("" as any)
                          }
                        />
                      </td>

                      <td className="td py-4 pl-3 pr-4 sm:pr-6 lg:pr-8 flex items-center gap-2">
                        <DropdownMenu
                          label={<DotsVerticalIcon className="w-4" />}
                          items={[
                            ...(isHaveActionPermission(
                              "",
                              "group_buy_management",
                              "view"
                            )
                              ? [
                                  {
                                    label: "View",
                                    onClick: () => {
                                      handleViewDetailAttendant(
                                        get(item, "id", "")
                                      );
                                    },
                                  },
                                ]
                              : []),

                            ...(handleCheckRenderAction(
                              item?.payment_status,
                              ATTENDANT_ACTION.TICK_PAID.value
                            )
                              ? [
                                  {
                                    label: "Tick Paid",
                                    onClick: () => {
                                      handleActionSingleAttendant(
                                        item,
                                        ATTENDANT_ACTION.TICK_PAID.value,
                                        ATTENDANT_ACTION.TICK_PAID.label
                                      );
                                    },
                                  },
                                ]
                              : []),

                            ...(handleCheckRenderAction(
                              item?.payment_status,
                              ATTENDANT_ACTION.TICK_UNPAID.value
                            )
                              ? [
                                  {
                                    label: "Tick Unpaid",
                                    onClick: () => {
                                      handleActionSingleAttendant(
                                        item,
                                        ATTENDANT_ACTION.TICK_UNPAID.value,
                                        ATTENDANT_ACTION.TICK_UNPAID.label
                                      );
                                    },
                                  },
                                ]
                              : []),

                            ...(handleCheckRenderAction(
                              item?.payment_status,
                              ATTENDANT_ACTION.MOVE.value
                            )
                              ? [
                                  {
                                    label: "Move",
                                    onClick: () => {
                                      handleMoveSingle(item);
                                    },
                                  },
                                ]
                              : []),

                            // ...(handleCheckRenderAction(
                            //   item?.payment_status,
                            //   ATTENDANT_ACTION.TICK_PAID_ALL.value
                            // )
                            //   ? [
                            //       {
                            //         label: "Tick Paid All",
                            //         onClick: () => {
                            //           handleActionSingleAttendant(
                            //             item,
                            //             ATTENDANT_ACTION.TICK_PAID_ALL.value,
                            //             ATTENDANT_ACTION.TICK_PAID_ALL.label
                            //           );
                            //         },
                            //       },
                            //     ]
                            //   : []),

                            // ...(handleCheckRenderAction(
                            //   item?.payment_status,
                            //   ATTENDANT_ACTION.TICK_UNPAID_ALL.value
                            // )
                            //   ? [
                            //       {
                            //         label: "Tick Unpaid All",
                            //         onClick: () => {
                            //           handleActionSingleAttendant(
                            //             item,
                            //             ATTENDANT_ACTION.TICK_UNPAID_ALL.value,
                            //             ATTENDANT_ACTION.TICK_UNPAID_ALL.label
                            //           );
                            //         },
                            //       },
                            //     ]
                            //   : []),

                            ...(handleCheckRenderAction(
                              item?.payment_status,
                              ATTENDANT_ACTION.REMOVE.value
                            )
                              ? [
                                  {
                                    label: "Remove",
                                    onClick: () => {
                                      handleActionSingleAttendant(
                                        item,
                                        ATTENDANT_ACTION.REMOVE.value,
                                        ATTENDANT_ACTION.REMOVE.label
                                      );
                                    },
                                  },
                                ]
                              : []),
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Pagination
        currentPage={get(paging, "current_page", 1)}
        totalPage={get(paging, "total_page", 0)}
        total={{
          totalItems: get(paging, "total_item", 0),
          total: get(paging, "total", 0),
        }}
        onChange={handleChangePage}
      />
    </section>
  );
};

export default AttendantDataTable;
